import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Spinner,
  FormFeedback,
} from "reactstrap";
import dayjs from "dayjs";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { candidateMarkAsHired } from "model/CandidateModel";

const markAsHiredSchema = Yup.object().shape({
  hiredDate: Yup.string().trim().required("Hired date is required"),
});

const CandidateMarkAsHiredModal = ({
  isOpen,
  onClose,
  candidateId,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [hiredDate, setHiredDate] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setHiredDate(dayjs().format("YYYY-MM-DD"));
    setErrors({});
  }, [isOpen]);

  const handleHiredDateChange = async (ev) => {
    const nextDate = ev.target.value;
    setHiredDate(nextDate);
    if (!errors.hiredDate) return;
    try {
      await Yup.reach(markAsHiredSchema, "hiredDate").validate(nextDate);
      setErrors((prev) => ({ ...prev, hiredDate: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, hiredDate: err.message }));
    }
  };

  const handleHiredDateBlur = async () => {
    try {
      await Yup.reach(markAsHiredSchema, "hiredDate").validate(hiredDate);
      setErrors((prev) => ({ ...prev, hiredDate: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, hiredDate: err.message }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const id = String(candidateId || "").trim();
    if (!id) {
      toast.error("Candidate ID not found");
      return;
    }

    const trimmedHiredDate = String(hiredDate ?? "").trim();
    try {
      await markAsHiredSchema.validate({ hiredDate: trimmedHiredDate }, { abortEarly: false });
      setErrors({});
    } catch (validationError) {
      const validationErrors = {};
      validationError?.inner?.forEach((errorItem) => {
        if (errorItem?.path && !validationErrors[errorItem.path]) {
          validationErrors[errorItem.path] = errorItem.message;
        }
      });
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const response = await candidateMarkAsHired(id, { hireDate: trimmedHiredDate });
      if (response?.success || response?.isSuccess) {
        toast.success(response?.message || "Candidate marked as hired");
        const responseData =
          response?.data && typeof response.data === "object" ? response.data : {};
        onSuccess?.({
          id,
          candidateId: id,
          profileStatus: responseData?.profileStatus ?? "hired",
          hireDate: responseData?.hireDate ?? trimmedHiredDate,
          ...responseData,
        });
        onClose?.();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    if (!submitting) onClose?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      backdrop="static"
      size="md"
      className="modal-full-height"
      centered
    >
      <Form onSubmit={handleSubmit}>
        <ModalBody className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="fw-bold mb-0">Mark as hired</h5>
            <Button close type="button" onClick={closeModal} disabled={submitting} />
          </div>
          <hr />

          <FormGroup>
            <Label>Hired date <span className="text-danger">*</span></Label>
            <Input
              type="date"
              value={hiredDate}
              onChange={handleHiredDateChange}
              onBlur={handleHiredDateBlur}
              invalid={!!errors.hiredDate}
            />
            <FormFeedback>{errors.hiredDate}</FormFeedback>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={closeModal} disabled={submitting}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-1" /> Submitting…
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CandidateMarkAsHiredModal;
